import React from 'react';
import { formatearYearMonth } from '../utils/historialLaboralUtils';

/**
 * Tarjeta de regularidad de aportes para minusvalía (240 meses totales, 120 consecutivos antes
 * del cese). Es el mismo cálculo que usa la herramienta de Historial Laboral
 * (RegularidadMinusvaliaDto / AporteService.calcularRegularidadMinusvaliaPreview) — este
 * componente es la única fuente visual de esa tarjeta; ImportarHistorialLaboralPage.js también
 * lo usa, así que no hay que duplicar el JSX.
 *
 * El link a Historial Laboral abre en una pestaña nueva (no navega el SPA) a propósito: este
 * componente aparece dentro de formularios de alta y fichas en progreso, y una navegación interna
 * destruye ese estado (selección de cliente, campos ya cargados) sin ninguna forma de recuperarlo
 * al volver atrás. Mismo criterio que ya usan VistaDetalladaAtencion.abrirHistorialLaboral y
 * PanelDiario para el mismo botón.
 */
export default function RegularidadMinusvalia({ regularidad, cargando, cuil }) {
    const abrirHistorialLaboral = () => {
        const cuilLimpio = String(cuil || '').replace(/\D/g, '');
        const url = `/herramientas/historial-laboral${cuilLimpio ? `?cuil=${cuilLimpio}` : ''}`;
        window.open(url, '_blank', 'noopener');
    };
    if (cargando) {
        return (
            <div className="card shadow-sm border-2 rounded-4 p-4" style={{ borderColor: '#4f46e5' }}>
                <span className="text-muted">
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Calculando regularidad de minusvalía...
                </span>
            </div>
        );
    }

    if (!regularidad) {
        return null;
    }

    const m = regularidad;
    const mesesTotales = Number(m.mesesTotales || 0);

    if (mesesTotales === 0) {
        return (
            <div className="card shadow-sm border-2 rounded-4 p-4" style={{ borderColor: '#6c757d' }}>
                <h6 className="fw-bold mb-2" style={{ color: '#6c757d' }}>Regularidad de minusvalía (invalidez)</h6>
                <div className="alert alert-warning border-0 rounded-3 mb-0">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    El cliente no tiene aportes cargados.{' '}
                    <button type="button" className="btn btn-link p-0 align-baseline" onClick={abrirHistorialLaboral}>
                        Ir a Historial Laboral
                    </button>
                </div>
            </div>
        );
    }

    const cumple = Boolean(m.cumpleAportes);
    const borde = cumple ? '#2e7d32' : '#6c757d';
    const badgeClass = cumple ? 'bg-success' : 'bg-secondary';
    const edadTexto = (m.mesesEfectivos || 0) > 0
        ? `${m.edadEstimadaAnios} años${m.edadEstimadaMeses ? ` ${m.edadEstimadaMeses} meses` : ''}`
        : 'No calculable';

    return (
        <div className="card shadow-lg border-2 rounded-4 p-4" style={{ borderColor: borde }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0" style={{ color: '#6c757d' }}>
                    Regularidad de minusvalía (invalidez)
                </h5>
                <span className={`badge rounded-pill px-3 py-2 ${badgeClass}`}>
                    {cumple ? 'CUMPLE APORTES' : 'NO CUMPLE APORTES'}
                </span>
            </div>

            <p className="mb-2" style={{ color: '#444' }}>{m.mensaje}</p>

            <div className="small" style={{ color: '#374151' }}>
                <div className="mb-1">
                    <strong>Meses totales (mín. 240):</strong>{' '}
                    <span style={{ color: m.cumple240 ? '#2e7d32' : '#c0392b', fontWeight: 700 }}>
                        {m.mesesTotales}
                    </span>
                </div>
                <div className="mb-1">
                    <strong>Meses efectivos consecutivos previos al cese (mín. 120):</strong>{' '}
                    <span style={{ color: m.cumple120Consecutivos ? '#2e7d32' : '#c0392b', fontWeight: 700 }}>
                        {m.mesesConsecutivosDesdeCese}
                    </span>
                </div>
                {m.periodoCese && (
                    <div className="mb-1">
                        <strong>Tramo consecutivo:</strong>{' '}
                        {formatearYearMonth(m.periodoInicioConsecutivo)} a {formatearYearMonth(m.periodoCese)}
                    </div>
                )}
                <div className="mb-1">
                    <strong>Edad prorrateada estimada:</strong> {edadTexto}{' '}
                    <small style={{ color: '#888' }}>
                        (aproximada - {m.mesesRelacionDependencia} + {m.mesesAutonomos} meses)
                    </small>
                </div>
                <div className="mt-2" style={{ color: '#6c757d' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    La invalidez &gt; 33% la evalúa la junta médica; acá sólo se calcula la regularidad de aportes.
                </div>
            </div>
        </div>
    );
}
